import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ChevronDown, ChevronRight, Scale } from "lucide-react";
import { toast } from "sonner";
import { inr, formatDate, todayISO } from "@/lib/utils2";
import BankSelector from "@/components/BankSelector";
import SettleDialog from "@/components/SettleDialog";

const empty = { party_id: "", date: todayISO(), amount: 0, mode: "Bank", bank_account_id: "", particulars: "", allocations: [] };

export default function PaymentsReceived() {
  const [payments, setPayments] = useState([]);
  const [parties, setParties] = useState([]);
  const [bills, setBills] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [partyBills, setPartyBills] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [origAllocs, setOrigAllocs] = useState({});
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleParty, setSettleParty] = useState("");
  const [period, setPeriod] = usePersistedState("period:pr", () => presetRange("this-month"));

  const load = async () => {
    const params = { date_from: period.from, date_to: period.to };
    const [p, pp, bb] = await Promise.all([
      api.get("/payments-received", { params }), api.get("/parties"), api.get("/bills"),
    ]);
    setPayments(p.data); setParties(pp.data); setBills(bb.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period.from, period.to]);

  const partyName = (id) => parties.find((p) => p.id === id)?.name || "—";

  // When party changes inside the dialog, refresh that party's outstanding bills
  useEffect(() => {
    if (!form.party_id) { setPartyBills([]); return; }
    (async () => {
      const { data } = await api.get(`/party/${form.party_id}/bills-detail`);
      // Balance available to THIS payment = bill balance + whatever this payment already gave the bill
      const rows = data.bills
        .map((b) => ({ ...b, available: Math.round((b.balance_due + (origAllocs[b.id] || 0)) * 100) / 100 }))
        .filter((b) => b.available > 0.01)
        .sort((a, b) => (a.bill_date < b.bill_date ? -1 : 1));
      setPartyBills(rows);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.party_id, form.id]);

  const allocTotal = useMemo(() =>
    form.allocations.reduce((s, a) => s + (Number(a.amount) || 0), 0), [form.allocations]);
  const onAccount = (Number(form.amount) || 0) - allocTotal;

  const toggleAlloc = (bill, checked) => {
    if (checked) {
      // Pre-fill with the smaller of: bill balance, money still unallocated on this payment (never goes minus)
      const amt = Math.round(Math.min(bill.available, Math.max(onAccount, 0)) * 100) / 100;
      setForm((s) => ({ ...s, allocations: [...s.allocations, { bill_id: bill.id, amount: amt }] }));
    } else {
      setForm((s) => ({ ...s, allocations: s.allocations.filter((a) => a.bill_id !== bill.id) }));
    }
  };

  const updateAllocAmt = (bill_id, amt) => {
    setForm((s) => ({
      ...s,
      allocations: s.allocations.map((a) => a.bill_id === bill_id ? { ...a, amount: parseFloat(amt) || 0 } : a),
    }));
  };

  const save = async () => {
    if (!form.party_id || !form.amount) return toast.error("Party and amount required");
    if (allocTotal > Number(form.amount) + 0.01) return toast.error(`Allocations ${inr(allocTotal)} exceed payment amount ${inr(form.amount)} — reduce a settle amount or use "Settle Bills" to combine several payments`);
    const payload = {
      ...form, amount: Number(form.amount),
      allocations: form.allocations.filter((a) => a.amount > 0),
    };
    try {
      if (form.id) {
        await api.put(`/payments-received/${form.id}`, payload);
        toast.success("On-account balance applied");
      } else {
        await api.post("/payments-received", payload);
        toast.success("Payment recorded");
      }
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Re-open dialog in "Apply on-account" mode against an existing payment.
  const applyOnAccount = (p) => {
    setOrigAllocs(Object.fromEntries((p.allocations || []).map((a) => [a.bill_id, Number(a.amount) || 0])));
    setForm({
      id: p.id,
      party_id: p.party_id || "",
      date: p.date,
      amount: p.amount,
      mode: p.mode || "Bank",
      bank_account_id: p.bank_account_id || "",
      particulars: p.particulars || "",
      allocations: (p.allocations || []).map((a) => ({ bill_id: a.bill_id, amount: Number(a.amount) || 0 })),
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!confirm("Delete this payment?")) return;
    await api.delete(`/payments-received/${id}`); load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] mx-auto" data-testid="payments-received-page">
      <PageHeader
        title="Payments Received"
        subtitle="Credit entries from parties — allocate against one or many bills, or keep on-account."
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="settle-bills-btn" variant="outline" onClick={() => { setSettleParty(""); setSettleOpen(true); }}
              className="rounded-sm border-brand-sand text-brand-ink hover:bg-brand-sand/10">
              <Scale className="h-4 w-4 mr-1.5" /> Settle Bills
            </Button>
            <Button data-testid="add-payment-recv-btn" onClick={() => { setOrigAllocs({}); setForm({ ...empty, date: todayISO() }); setOpen(true); }}
              className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Record Payment
            </Button>
          </div>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none p-4 mb-5">
        <PeriodPicker value={period} onChange={(p) => setPeriod({ from: p.from, to: p.to })}
          defaultPreset="this-month" testIdPrefix="pr-period" />
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="w-8 px-3 py-3"></th>
              <th className="text-left px-3 py-3">Date</th>
              <th className="text-left px-3 py-3">Party</th>
              <th className="text-left px-3 py-3">Mode</th>
              <th className="text-left px-3 py-3">Particulars</th>
              <th className="text-right px-3 py-3">Amount</th>
              <th className="text-center px-3 py-3">Bills Settled</th>
              <th className="text-right px-3 py-3">On Account</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-brand-muted">No payments yet.</td></tr>}
            {payments.map((p) => {
              const isOpen = expanded[p.id];
              const allocs = p.allocations || [];
              return (
                <React.Fragment key={p.id}>
                  <tr className="border-t border-brand-line hover:bg-secondary/40">
                    <td className="px-3 py-3">
                      {allocs.length > 0 ? (
                        <button onClick={() => setExpanded((s) => ({ ...s, [p.id]: !s[p.id] }))} className="text-brand-muted hover:text-brand-ink">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-3">{formatDate(p.date)}</td>
                    <td className="px-3 py-3 font-medium">{partyName(p.party_id)}</td>
                    <td className="px-3 py-3 text-brand-muted">{p.mode}</td>
                    <td className="px-3 py-3 text-brand-muted">{p.particulars}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-brand-forest font-medium">{inr(p.amount)}</td>
                    <td className="px-3 py-3 text-center">
                      {allocs.length > 0
                        ? <Badge className="bg-brand-forest text-white rounded-sm">{allocs.length} bill{allocs.length > 1 ? "s" : ""}</Badge>
                        : <span className="text-xs text-brand-muted">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-brand-sand">{p.on_account_amount > 0 ? inr(p.on_account_amount) : "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {p.on_account_amount > 0.01 && (
                          <>
                            <Button size="sm" variant="outline"
                              className="h-7 rounded-sm text-[11px] border-brand-sand text-brand-sand hover:bg-brand-sand/10"
                              onClick={() => applyOnAccount(p)}
                              data-testid={`apply-recv-onacc-${p.id}`}
                            >
                              Apply {inr(p.on_account_amount)}
                            </Button>
                            <Button size="sm" variant="ghost"
                              className="h-7 rounded-sm text-[11px] text-brand-forest"
                              onClick={() => { setSettleParty(p.party_id); setSettleOpen(true); }}
                              data-testid={`settle-recv-${p.id}`}
                            >
                              <Scale className="h-3 w-3 mr-1" /> Settle
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-terracotta" onClick={() => remove(p.id)} data-admin-only="true">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && allocs.length > 0 && (
                    <tr className="bg-secondary/40">
                      <td colSpan={9} className="px-12 py-3">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted mb-2">Allocations</div>
                        <table className="w-full text-xs">
                          <thead className="text-brand-muted">
                            <tr><th className="text-left py-1">Bill No</th><th className="text-right py-1">Amount Settled</th></tr>
                          </thead>
                          <tbody>
                            {allocs.map((a, i) => (
                              <tr key={i}><td className="py-1 font-mono">{a.bill_number || a.bill_id?.slice(0, 8)}</td><td className="py-1 text-right tabular-nums">{inr(a.amount)}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {form.id ? "Apply On-Account Balance" : "Record Payment Received"}
            </DialogTitle>
            {form.id && (
              <div className="text-xs text-brand-muted">
                Allocate the remaining on-account balance of this payment against the party's outstanding bills. Payment amount and bank are locked.
              </div>
            )}
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className={`grid grid-cols-2 gap-4 ${form.id ? "opacity-70" : ""}`}>
              <div>
                <Label>Party *</Label>
                <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v, allocations: [] })} disabled={!!form.id}>
                  <SelectTrigger data-testid="payment-party-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue placeholder="Select party" /></SelectTrigger>
                  <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={!!form.id} /></div>
              <div><Label>Amount *</Label><Input data-testid="payment-amount" type="number" step="0.01" className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} disabled={!!form.id} /></div>
              <div><Label>Mode</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} disabled={!!form.id} /></div>
              <div className="col-span-2">
                <Label>Bank Account (receiving)</Label>
                <BankSelector value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} testId="recv-bank-selector" />
              </div>
              <div className="col-span-2"><Label>Particulars</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.particulars} onChange={(e) => setForm({ ...form, particulars: e.target.value })} /></div>
            </div>

            {form.party_id && (
              <div className="border-t border-brand-line pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Allocate to bills (optional — else stays on-account)</div>
                  <div className="text-xs">
                    Allocated <span className="font-semibold tabular-nums">{inr(allocTotal)}</span> · On account <span className={`font-semibold tabular-nums ${onAccount < 0 ? "text-brand-terracotta" : "text-brand-sand"}`}>{inr(onAccount)}</span>
                  </div>
                </div>
                {partyBills.length === 0 ? (
                  <div className="text-xs text-brand-muted p-4 text-center bg-secondary/40 rounded-sm">No outstanding bills for this party — payment will be on-account.</div>
                ) : (
                  <div className="border border-brand-line rounded-sm">
                    <table className="w-full text-xs">
                      <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                        <tr><th className="px-2 py-2 w-8"></th><th className="text-left px-2 py-2">Bill No</th><th className="text-left px-2 py-2">Date</th><th className="text-right px-2 py-2">Total</th><th className="text-right px-2 py-2">Balance Available</th><th className="text-right px-2 py-2 w-32">Settle Amount</th></tr>
                      </thead>
                      <tbody>
                        {partyBills.map((b) => {
                          const alloc = form.allocations.find((a) => a.bill_id === b.id);
                          const checked = !!alloc;
                          return (
                            <tr key={b.id} className={`border-t border-brand-line ${checked ? "bg-brand-sand/10" : ""}`}>
                              <td className="px-2 py-2"><Checkbox checked={checked} onCheckedChange={(v) => toggleAlloc(b, !!v)} data-testid={`alloc-bill-${b.id}`} /></td>
                              <td className="px-2 py-2 font-mono">{b.bill_number}</td>
                              <td className="px-2 py-2">{formatDate(b.bill_date)}</td>
                              <td className="px-2 py-2 text-right tabular-nums">{inr(b.total_amount)}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-brand-terracotta">{inr(b.available)}</td>
                              <td className="px-2 py-2 text-right">
                                {checked && (
                                  <Input type="number" step="0.01" value={alloc.amount}
                                    onChange={(e) => updateAllocAmt(b.id, e.target.value)}
                                    className="h-8 w-28 rounded-sm text-right tabular-nums bg-white text-xs ml-auto" />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-payment-recv-btn" className="rounded-sm bg-brand-forest text-white">
              {form.id ? "Apply Allocations" : "Save Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SettleDialog
        open={settleOpen} onOpenChange={setSettleOpen}
        title="Settle Bills from On-Account"
        entityLabel="Party" entities={parties} initialEntityId={settleParty}
        testIdPrefix="settle-recv" allocKey="bill_id"
        endpoint={(pid) => `/party/${pid}/settle`}
        loadRows={async (pid) => {
          const { data } = await api.get(`/party/${pid}/bills-detail`);
          const rows = data.bills.filter((b) => b.balance_due > 0.01)
            .sort((a, b) => (a.bill_date < b.bill_date ? -1 : 1))
            .map((b) => ({ id: b.id, balance: b.balance_due, bill_number: b.bill_number, bill_date: b.bill_date, total_amount: b.total_amount, received_total: b.received_total }));
          return { onAccount: data.on_account, rows };
        }}
        columns={[
          { key: "bill_number", label: "Bill No", className: "font-mono" },
          { key: "bill_date", label: "Date", render: (r) => formatDate(r.bill_date) },
          { key: "total_amount", label: "Total", align: "right", render: (r) => inr(r.total_amount) },
          { key: "received_total", label: "Received", align: "right", render: (r) => inr(r.received_total) },
        ]}
        onDone={load}
      />
    </div>
  );
}
